/**
 * Embed mode (UPT-1454) — when a page is rendered inside the master-detail
 * RightDrawer iframe, the app chrome (form header banner, page footer) is
 * redundant: the drawer already provides the title bar and badges.
 *
 * The drawer appends `?embed=1` to the iframe URL; components check
 * `isEmbeddedView()` and skip their chrome.
 */

export const EMBED_QUERY_PARAM = 'embed';
export const EMBED_QUERY_VALUE = '1';

/** True when the current window is an embedded (drawer iframe) view. SSR-safe. */
export const isEmbeddedView = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  return new URLSearchParams(window.location.search).get(EMBED_QUERY_PARAM) === EMBED_QUERY_VALUE;
};

/** Append the embed marker to a canonical URL (used as drawer iframe src). */
export const toEmbeddedUrl = (url: string): string =>
  `${url}${url.includes('?') ? '&' : '?'}${EMBED_QUERY_PARAM}=${EMBED_QUERY_VALUE}`;

/** Strip the embed marker (e.g. before full-page navigation from the drawer). */
export const fromEmbeddedUrl = (url: string): string =>
  url
    .replace(/([?&])embed=1&?/, (match, sep: string) => (sep === '?' ? '?' : ''))
    .replace(/[?&]$/, '');

// ── Drawer iframe → parent events (Olivier 23/08) ───────────────────────────
// When a record is deleted from an embedded form, the iframe must not navigate
// to the entity list inside the drawer: it tells the parent, which closes the
// drawer and refreshes the master record.
export const EMBEDDED_RECORD_DELETED_EVENT = 'geode:embedded-record-deleted';

export type EmbeddedRecordDeletedDetail = {
  form?: string;
  id?: string | number;
};

/** Post a "record deleted" event to the parent window (no-op outside an iframe). */
export const notifyEmbeddedRecordDeleted = (detail: EmbeddedRecordDeletedDetail): void => {
  if (typeof window === 'undefined' || window.parent === window) {
    return;
  }
  window.parent.postMessage({ type: EMBEDDED_RECORD_DELETED_EVENT, ...detail }, window.location.origin);
};

// ── Drawer iframe → parent "open entity" (UAT 30/08, Olivier) ───────────────
// 'Page d'un livre' : quand une fiche EMBARQUEE veut ouvrir une entite liee,
// elle ne doit pas empiler un drawer DANS l'iframe (il resterait sous le
// bandeau du drawer parent) — elle demande au parent d'ouvrir la page
// suivante au niveau haut, qui couvre alors TOUTE la surface.
export const EMBEDDED_OPEN_ENTITY_EVENT = 'geode:embedded-open-entity';

export type EmbeddedOpenEntityDetail = {
  entity: string;
  recordId: string;
  url?: string;
  record?: Record<string, unknown>;
};

/** Ask the parent window to open a linked-entity sheet (no-op outside an iframe). */
export const notifyEmbeddedOpenEntity = (detail: EmbeddedOpenEntityDetail): boolean => {
  if (typeof window === 'undefined' || window.parent === window) {
    return false;
  }
  window.parent.postMessage({ type: EMBEDDED_OPEN_ENTITY_EVENT, ...detail }, window.location.origin);
  return true;
};

// ── Drawer iframe → parent "close" (UAT 30/08) ──────────────────────────────
// Le bouton CLOSE d'une fiche embarquee fermait... la fiche DANS l'iframe
// (goBack/push '/') et atterrissait sur le home. En embed, CLOSE = fermer la
// page (le drawer) du dessus, donc on demande au parent.
export const EMBEDDED_CLOSE_EVENT = 'geode:embedded-close';

/** Ask the parent window to close the drawer hosting this sheet (no-op outside an iframe). */
export const notifyEmbeddedClose = (): boolean => {
  if (typeof window === 'undefined' || window.parent === window) {
    return false;
  }
  window.parent.postMessage({ type: EMBEDDED_CLOSE_EVENT }, window.location.origin);
  return true;
};

// ── Dispatcher unique du close (UAT 31/08) ─────────────────────────────────
// Plusieurs composants d'une meme window ecoutaient EMBEDDED_CLOSE_EVENT en
// parallele : la fiche budgetline embarquee fermait son drawer detail ET
// useLinkedEntities (sans drawer ouvert) RELAYAIT l'evenement vers le haut,
// fermant aussi le drawer budgetline du parent. Regle : UN dispatcher par
// window — le premier consumer avec un drawer ouvert consomme ; sinon (et
// seulement sinon) l'evenement est relaye au parent si nous sommes embarques.
type EmbeddedCloseConsumer = { isOpen: () => boolean; close: () => void };
const embeddedCloseConsumers = new Set<EmbeddedCloseConsumer>();
let embeddedCloseDispatcherInstalled = false;

const ensureEmbeddedCloseDispatcher = (): void => {
  if (embeddedCloseDispatcherInstalled || typeof window === 'undefined') {
    return;
  }
  embeddedCloseDispatcherInstalled = true;
  window.addEventListener('message', (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    const data = event.data as { type?: string };
    if (!data || data.type !== EMBEDDED_CLOSE_EVENT) return;
    for (const consumer of embeddedCloseConsumers) {
      if (consumer.isOpen()) {
        consumer.close();
        return;
      }
    }
    if (isEmbeddedView()) {
      notifyEmbeddedClose();
    }
  });
};

/**
 * Register a drawer host for close requests. The dispatcher hands the event
 * to the FIRST registered consumer whose drawer is open; the relay upwards
 * only happens when nobody consumed. Returns the unregister function.
 */
export const registerEmbeddedCloseConsumer = (consumer: EmbeddedCloseConsumer): (() => void) => {
  ensureEmbeddedCloseDispatcher();
  embeddedCloseConsumers.add(consumer);
  return () => { embeddedCloseConsumers.delete(consumer); };
};

/** Subscribe (in the parent) to "close" requests from drawer iframes. Returns the unsubscribe. */
export const subscribeEmbeddedClose = (handler: () => void): (() => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }
  const listener = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    const data = event.data as { type?: string };
    if (!data || data.type !== EMBEDDED_CLOSE_EVENT) return;
    handler();
  };
  window.addEventListener('message', listener);
  return () => window.removeEventListener('message', listener);
};

// ── Data refresh sans reload (UAT 31/08) ────────────────────────────────────
// Au retour d'un drawer enfant (qui a pu sauver), l'hote NE recharge PAS la
// page : il rafraichit les DONNEES du record affiche. Deux canaux :
//   - CustomEvent local REFRESH_RECORD_EVENT (meme window) ;
//   - postMessage EMBEDDED_REFRESH_EVENT (parent -> iframe embarquee).
// useEntityForm ecoute les deux et appelle me.reloadRecord().
export const REFRESH_RECORD_EVENT = 'geode:refresh-record';
export const EMBEDDED_REFRESH_EVENT = 'geode:embedded-refresh';

/** Ask the CURRENT window's entity form(s) to re-fetch their record. */
export const notifyRefreshRecord = (): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(REFRESH_RECORD_EVENT));
};

/** Ask an embedded iframe's entity form to re-fetch its record. */
export const notifyEmbeddedRefresh = (frame: HTMLIFrameElement): void => {
  try {
    frame.contentWindow?.postMessage({ type: EMBEDDED_REFRESH_EVENT }, window.location.origin);
  } catch { /* frame gone */ }
};

/** Subscribe to both refresh channels (local CustomEvent + embed postMessage). */
export const subscribeRefreshRecord = (handler: () => void): (() => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }
  const customListener = () => handler();
  const messageListener = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    const data = event.data as { type?: string };
    if (!data || data.type !== EMBEDDED_REFRESH_EVENT) return;
    handler();
  };
  window.addEventListener(REFRESH_RECORD_EVENT, customListener);
  window.addEventListener('message', messageListener);
  return () => {
    window.removeEventListener(REFRESH_RECORD_EVENT, customListener);
    window.removeEventListener('message', messageListener);
  };
};

/** Subscribe (in the parent) to "open entity" requests from drawer iframes. Returns the unsubscribe. */
export const subscribeEmbeddedOpenEntity = (
  handler: (detail: EmbeddedOpenEntityDetail) => void
): (() => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }
  const listener = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    const data = event.data as { type?: string } & EmbeddedOpenEntityDetail;
    if (!data || data.type !== EMBEDDED_OPEN_ENTITY_EVENT) return;
    handler(data);
  };
  window.addEventListener('message', listener);
  return () => window.removeEventListener('message', listener);
};

/** Subscribe (in the parent) to "record deleted" events coming from drawer iframes. Returns the unsubscribe. */
export const subscribeEmbeddedRecordDeleted = (
  handler: (detail: EmbeddedRecordDeletedDetail) => void
): (() => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }
  const listener = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    const data = event.data as { type?: string } & EmbeddedRecordDeletedDetail;
    if (!data || data.type !== EMBEDDED_RECORD_DELETED_EVENT) return;
    handler({ form: data.form, id: data.id });
  };
  window.addEventListener('message', listener);
  return () => window.removeEventListener('message', listener);
};
